import type { ReactNode, RefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { useFullscreen } from '../hooks/useFullscreen';
import { clampToViewport, useResizable } from '../hooks/useResizable';
import { selectCallPanelRect, setCallPanelRect } from '../store';

interface FloatingTelehealthPanelShellProps {
  children: (props: FloatingTelehealthPanelShellRenderProps) => ReactNode;
}

export interface FloatingTelehealthPanelShellRenderProps {
  width: number;
  height: number;
  isCompact: boolean;
  compactControls: boolean;
  fullscreen: ReturnType<typeof useFullscreen>;
  panelRef: RefObject<HTMLDivElement | null>;
}

const DEFAULT_VW_FRACTION = 0.33;
const ASPECT_RATIO = 0.75; // 4:3
const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;
const MAX_VIEWPORT_FRACTION = 0.8;
const WIDTH_BREAKPOINT = 400;
const EDGE_MARGIN = 24;

function getDefaultRect() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.max(MIN_WIDTH, Math.round(vw * DEFAULT_VW_FRACTION));
  const height = Math.max(MIN_HEIGHT, Math.round(width * ASPECT_RATIO));
  return {
    width,
    height,
    x: vw - width - EDGE_MARGIN,
    y: vh - height - EDGE_MARGIN,
  };
}

export function FloatingTelehealthPanelShell({ children }: FloatingTelehealthPanelShellProps) {
  const dispatch = useAppDispatch();
  const storedRect = useAppSelector(selectCallPanelRect);
  const panelRef = useRef<HTMLDivElement>(null);
  const fullscreen = useFullscreen(panelRef);

  const initialRect = useMemo(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return storedRect ? clampToViewport(storedRect, vw, vh) : getDefaultRect();
    // Only compute once on mount. The resizable hook owns live movement after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [maxWidth, setMaxWidth] = useState(() =>
    Math.round(window.innerWidth * MAX_VIEWPORT_FRACTION),
  );
  const [maxHeight, setMaxHeight] = useState(() =>
    Math.round(window.innerHeight * MAX_VIEWPORT_FRACTION),
  );

  const handleRectChange = useCallback(
    (rect: { width: number; height: number; x: number; y: number }) => {
      dispatch(setCallPanelRect(rect));
    },
    [dispatch],
  );

  const {
    width,
    height,
    x,
    y,
    isResizing,
    isDragging,
    resizeHandles,
    dragHandleProps,
    setRect: setResizableRect,
  } = useResizable({
    initialWidth: initialRect.width,
    initialHeight: initialRect.height,
    initialX: initialRect.x,
    initialY: initialRect.y,
    minWidth: MIN_WIDTH,
    minHeight: MIN_HEIGHT,
    maxWidth,
    maxHeight,
    onResizeEnd: handleRectChange,
    onMoveEnd: handleRectChange,
  });

  useEffect(() => {
    let rafId: number;
    const handleViewportResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const newMaxW = Math.round(vw * MAX_VIEWPORT_FRACTION);
        const newMaxH = Math.round(vh * MAX_VIEWPORT_FRACTION);
        setMaxWidth(newMaxW);
        setMaxHeight(newMaxH);

        setResizableRect(prev => {
          const clampedW = Math.max(MIN_WIDTH, Math.min(prev.width, newMaxW));
          const clampedH = Math.max(MIN_HEIGHT, Math.min(prev.height, newMaxH));
          const clamped = clampToViewport(
            { width: clampedW, height: clampedH, x: prev.x, y: prev.y },
            vw,
            vh,
          );
          if (
            clamped.x !== prev.x ||
            clamped.y !== prev.y ||
            clampedW !== prev.width ||
            clampedH !== prev.height
          ) {
            dispatch(setCallPanelRect(clamped));
            return clamped;
          }
          return prev;
        });
      });
    };

    window.addEventListener('resize', handleViewportResize);
    return () => {
      window.removeEventListener('resize', handleViewportResize);
      cancelAnimationFrame(rafId);
    };
  }, [dispatch, setResizableRect]);

  const isInteracting = isResizing || isDragging;
  const isCompact = width < WIDTH_BREAKPOINT;
  const compactControls = !fullscreen.isFullscreen && width < 600;

  return (
    <Box
      sx={{
        position: 'fixed',
        left: x,
        top: y,
        width,
        height,
        zIndex: 1299,
        transition: isInteracting
          ? 'none'
          : 'width 200ms ease, height 200ms ease, left 200ms ease, top 200ms ease',
      }}
    >
      {resizeHandles}

      <Box
        ref={panelRef}
        {...dragHandleProps}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          cursor: dragHandleProps.style.cursor,
          position: 'relative',
        }}
      >
        {children({ width, height, isCompact, compactControls, fullscreen, panelRef })}
      </Box>
    </Box>
  );
}
